import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Trophy, Loader2, ArrowLeft, Mail, KeyRound, CheckCircle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'code' | 'success'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const { data } = await authApi.forgotPassword(email);
      toast.success('Solicitacao enviada!');
      setStep('code');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao solicitar recuperacao');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('As senhas nao coincidem');
      return;
    }
    if (newPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setIsLoading(true);
    try {
      await authApi.resetPassword({ email, code, new_password: newPassword });
      toast.success('Senha alterada com sucesso!');
      setStep('success');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setIsLoading(false);
    }
  };

  /* ETAPA VISUAL 7 — mesma correção do login: `flex-col` + `justify-center`
     evitam o corte do topo quando o formulário passa da janela, e o padding
     vertical subiu de 16px (`p-4`) para 32/40px. */
  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center px-4 py-6 sm:py-8">
      <div className="w-full max-w-md">
        {/* Logo — mesmo desenho das outras telas de autenticação. A linha de
            apoio saiu porque o próprio cartão já se apresenta ("Esqueceu sua
            senha?"). */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#B8860B] mb-2">
            <Trophy className="w-6 h-6 text-black" />
          </div>
          <h1 className="text-2xl font-bold text-white font-['Chivo']">BJJ Manager</h1>
        </div>

        <div className="bg-[#141414] border border-[#27272A] rounded-xl p-5" data-testid="forgot-password-page">
          {/* Step 1: Enter Email */}
          {step === 'email' && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                  <Mail size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white font-['Chivo']">Esqueceu sua senha?</h2>
                  <p className="text-[#71717A] text-sm">Informe seu email para receber o codigo</p>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleRequestCode} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Email cadastrado</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    required
                    data-testid="forgot-email"
                    className="bg-[#1A1A1A] border-[#27272A] text-white placeholder:text-[#71717A] focus:border-[#D4AF37]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  data-testid="forgot-submit"
                  className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold py-3"
                >
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Solicitar Codigo'}
                </Button>
              </form>

              <p className="text-[#71717A] text-xs mt-4 text-center">
                O codigo sera gerado e disponibilizado pelo administrador do sistema.
              </p>
            </>
          )}

          {/* Step 2: Enter Code + New Password */}
          {step === 'code' && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                  <KeyRound size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white font-['Chivo']">Redefinir Senha</h2>
                  <p className="text-[#71717A] text-sm">Insira o codigo e sua nova senha</p>
                </div>
              </div>

              <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <p className="text-blue-400 text-sm">
                  Codigo enviado para <strong>{email}</strong>. Solicite ao administrador o codigo de 6 digitos.
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="reset-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Codigo de 6 digitos</Label>
                  <Input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    required
                    maxLength={6}
                    data-testid="reset-code"
                    className="bg-[#1A1A1A] border-[#27272A] text-white text-center text-2xl tracking-[0.5em] font-mono placeholder:text-[#71717A] placeholder:tracking-[0.5em] focus:border-[#D4AF37]"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Nova Senha</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimo 6 caracteres"
                    required
                    data-testid="reset-new-password"
                    className="bg-[#1A1A1A] border-[#27272A] text-white placeholder:text-[#71717A] focus:border-[#D4AF37]"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Confirmar Nova Senha</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repita a nova senha"
                    required
                    data-testid="reset-confirm-password"
                    className="bg-[#1A1A1A] border-[#27272A] text-white placeholder:text-[#71717A] focus:border-[#D4AF37]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading || code.length !== 6}
                  data-testid="reset-submit"
                  className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold py-3"
                >
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Redefinir Senha'}
                </Button>
              </form>

              <button
                onClick={() => { setStep('email'); setError(''); }}
                className="w-full mt-3 text-sm text-[#71717A] hover:text-white text-center"
                data-testid="back-to-email"
              >
                Solicitar novo codigo
              </button>
            </>
          )}

          {/* Step 3: Success */}
          {step === 'success' && (
            <div className="text-center py-4" data-testid="reset-success">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck size={32} className="text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2 font-['Chivo']">Senha Alterada!</h2>
              <p className="text-[#A1A1AA] mb-6">Sua senha foi redefinida com sucesso. Voce ja pode fazer login.</p>
              <Button
                onClick={() => navigate('/login')}
                data-testid="go-to-login"
                className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold py-3"
              >
                Ir para Login
              </Button>
            </div>
          )}

          {/* Back to login */}
          {step !== 'success' && (
            <div className="mt-6 text-center">
              <Link to="/login" className="text-[#A1A1AA] hover:text-white text-sm inline-flex items-center gap-1" data-testid="back-to-login">
                <ArrowLeft size={16} /> Voltar para o Login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
